/**
 * Pure grouping/labeling model for the Engine scenario picker.
 * Kept component-free so vitest covers it and fast-refresh stays clean.
 */

import type { Scenario } from '../../../lib/planningEngineModule.types';

/**
 * Disambiguation labels for scenarios whose (group, name) collide — e.g. three
 * identical "Delay 4wk" hypotheses minted before dedupe existed. Returns
 * id → short created-date label, only for colliding rows.
 */
export function scenarioDupeDateLabels(scenarios: Scenario[]): Map<string, string> {
  const byKey = new Map<string, Scenario[]>();
  for (const s of scenarios) {
    const key = `${s.planCycleId ?? 'what-if'}::${s.name.trim().toLowerCase()}`;
    const list = byKey.get(key) ?? [];
    list.push(s);
    byKey.set(key, list);
  }
  const labels = new Map<string, string>();
  for (const list of byKey.values()) {
    if (list.length < 2) continue;
    const sorted = [...list].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
    sorted.forEach((s, i) => {
      const d = new Date(s.createdAt);
      const date = Number.isNaN(d.getTime())
        ? ''
        : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      labels.set(s.id, date ? `${date} · #${i + 1}` : `#${i + 1}`);
    });
  }
  return labels;
}

export interface GroupedScenarios {
  cycleGroups: { cycleId: string; scenarios: Scenario[] }[];
  whatIf: Scenario[];
}

/** Group scenarios: cycle-bound groups (picker order), then unbound what-ifs. */
export function groupScenariosByCycle(scenarios: Scenario[]): GroupedScenarios {
  const byCycle = new Map<string, Scenario[]>();
  const whatIf: Scenario[] = [];
  for (const s of scenarios) {
    if (s.planCycleId) {
      const list = byCycle.get(s.planCycleId) ?? [];
      list.push(s);
      byCycle.set(s.planCycleId, list);
    } else {
      whatIf.push(s);
    }
  }
  return {
    cycleGroups: [...byCycle.entries()].map(([cycleId, list]) => ({ cycleId, scenarios: list })),
    whatIf,
  };
}

/**
 * zero/one/few/many (.cursor/rules/zero-one-few-many.mdc): any picker loading
 * from a store must offer search once the set can reach 8+. This filters an
 * already-grouped result by scenario name, dropping cycle groups left empty.
 */
export function filterGroupedScenarios(grouped: GroupedScenarios, query: string): GroupedScenarios {
  const q = query.trim().toLowerCase();
  if (!q) return grouped;
  const matches = (s: Scenario) => s.name.toLowerCase().includes(q);
  return {
    cycleGroups: grouped.cycleGroups
      .map((g) => ({ ...g, scenarios: g.scenarios.filter(matches) }))
      .filter((g) => g.scenarios.length > 0),
    whatIf: grouped.whatIf.filter(matches),
  };
}
